/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http'
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit,
  inject,
} from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { ButtonComponent } from '@progress/kendo-angular-buttons'
import {
  DateRangePopupComponent,
  DateRangeService,
  MultiViewCalendarComponent,
} from '@progress/kendo-angular-dateinputs'
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
} from '@progress/kendo-data-query'
import { PageMargin } from '@progress/kendo-drawing/dist/npm/pdf'
import {
  LineChart,
  SunburstChart,
  PieChart,
  HeatmapChart,
} from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent,
  VisualMapComponent,
  DataZoomComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { GridOption } from 'echarts/types/dist/shared'
import _ from 'lodash-es'
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  finalize,
  forkJoin,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs'

import {
  Enums,
  Environment,
  HuntCoverage,
  MitreHeatMapCoverage,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import { DialogRef, DialogService, ThemeService } from '@hyperkit'

import {
  addNewLinesBetweenWords,
  mapOutcomeColor,
  mapPriorityColor,
  mapStatusColor,
} from '../../../../helpers/utils'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service'
import {
  MITRETactics,
  MitreComponent,
  processMitreEnterpriseJSON,
} from '../../../research/components/filter/components/dashboard/mitre/mitre.component'
import {
  ResolvedSearchResultData,
  SearchDataService,
} from '../../../research/services/search-data.service'
import {
  SearchStateService,
  TagDictionary,
} from '../../../research/services/search-state.service'
import { SearchUrlService } from '../../../research/services/search-url.service'
import { CompanyService } from '../../../shared/services/company.service'
import { EnvironmentService } from '../../../shared/services/environment.service'
import {
  AssignedHunts,
  HuntMetricsService,
} from '../../../shared/services/hunt-metrics.service'
import { UserService } from '../../../shared/services/user.service'
import {
  ExtendedState,
  HuntFilterFields,
  HuntStateService,
} from '../hunt-list/hunt-state-service'
import { HuntUrlService } from '../hunt-list/hunt-url-service'

import {
  ChartUtils,
  findLongestHeatmapColumn,
  prepareReportSections,
  resetReportSections,
  SunburstData,
} from './charts/chart-utils'
import * as mitreHeatmapChartJson from './charts/mitre-heatmap-chart.json'
import * as noDataChartJson from './charts/no-data-chart.json'
import * as pieChartJson from './charts/pie-chart.json'
import * as sunburstChartJson from './charts/sunburst-chart.json'
import * as threatPieChartJson from './charts/threat-pie-chart.json'
import {
  DashboardStateService,
  DashboardState,
  PresetDateFilters,
  DashboardFilterFields,
  DashboardChartExports,
} from './dashboard-state-service'
import {
  CyborgRouterSearchLink,
  DashboardUrlService,
} from './dashboard-url-service'
import { ExportOptionsDialogComponent } from './export-options-dialog/export-options-dialog.component'

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  VisualMapComponent,
  LineChart,
  CanvasRenderer,
  GraphicComponent,
  SunburstChart,
  PieChart,
  HeatmapChart,
  DataZoomComponent,
])

let cachedMitreSubject: ReplaySubject<MITRETactics[]>

@Component({
  standalone: false,
  providers: [DateRangeService],
  templateUrl: './dashboard.component.html',
  styleUrls: ['dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  huntMetricsService = inject(HuntMetricsService)
  huntUrlService = inject(HuntUrlService)
  userService = inject(UserService)
  dashboardStateService = inject(DashboardStateService)
  dashboardUrlService = inject(DashboardUrlService)
  private activatedRoute = inject(ActivatedRoute)
  environmentService = inject(EnvironmentService)
  companyService = inject(CompanyService)
  http = inject(HttpClient)
  huntStateService = inject(HuntStateService)
  private router = inject(Router)
  searchStateService = inject(SearchStateService)
  searchUrlService = inject(SearchUrlService)
  searchDataService = inject(SearchDataService)
  httpClient = inject(HttpClient)
  trackingService = inject(TrackingService)
  dialogService = inject(DialogService)
  private cdRef = inject(ChangeDetectorRef)
  themeService = inject(ThemeService)

  public tagTypes = Enums.CyborgTagTypeEnum
  public showAllMITREToggleSubject = new BehaviorSubject<boolean>(false)
  @ViewChild('anchor', { read: ElementRef }) public anchor: ElementRef
  @ViewChild('anchor', { read: ButtonComponent })
  public anchorComponent: ButtonComponent
  @ViewChild('popup', { read: DateRangePopupComponent })
  public popup: DateRangePopupComponent
  @ViewChild('calendar', { read: MultiViewCalendarComponent })
  public calendar: MultiViewCalendarComponent
  public dashboardFields = DashboardFilterFields
  public chartColors = ChartUtils.sunburstColors
  public huntCount: number
  public isLoaded = false
  public margins: PageMargin = {
    top: '1in',
    bottom: '1in',
    left: '1in',
    right: '1in',
  }
  public heatmapHeight = 800
  public huntActorCoverage$: Observable<{ results: HuntCoverage[] }>
  public huntMITRECoverage$: Observable<{ results: HuntCoverage[] }>
  public huntThreatCoverage$: Observable<{ results: HuntCoverage[] }>
  public longestHeatmapColumn: number
  public noDataMessage = 'No data to display'
  public isExporting = false

  public huntStatuses: {
    value: number
    name: Enums.StatusEnum
    itemStyle: { color: string }
  }[]
  public huntOutcomes: {
    value: number
    name: Enums.OutcomeEnum
    itemStyle: { color: string }
  }[]
  public huntPriorities: {
    value: number
    name: Enums.PriorityEnum
    itemStyle: { color: string }
  }[]

  public myAssignedHunts$: Observable<any>
  public myCompletedHunts: Observable<any>
  public environments: Environment[]

  // Coverage
  public actorSunburst: echarts.ECharts
  public actorSunburstOptions = _.cloneDeep(sunburstChartJson)
  public mitreSunburst: echarts.ECharts
  public mitreSunburstOptions = _.cloneDeep(sunburstChartJson)
  public threatPie: echarts.ECharts
  public threatPieOptions = threatPieChartJson
  public noDataOptions = noDataChartJson

  // MITRE
  public mitreHeatmap: echarts.ECharts
  public mitreHeatmapOptions = _.cloneDeep(mitreHeatmapChartJson)

  // The heatmap's coverage-intensity gradient (low -> high) is stored as a
  // static hex array in the JSON config, which can't reference CSS custom
  // properties -- patch it here with the resolved semantic tokens so it
  // reflects the active theme instead of the old hardcoded red shades.
  private patchMitreHeatmapPalette(): void {
    const styles = getComputedStyle(document.documentElement)
    this.mitreHeatmapOptions.visualMap.color = [
      styles.getPropertyValue('--foreground-muted').trim(),
      styles.getPropertyValue('--foreground-accent-info').trim(),
      styles.getPropertyValue('--foreground-accent-positive').trim(),
    ]
  }

  // Metrics
  public statusPie: echarts.ECharts
  public statusPieOptions = _.cloneDeep(pieChartJson)
  public outcomePie: echarts.ECharts
  public outcomePieOptions = _.cloneDeep(pieChartJson)
  public priorityPie: echarts.ECharts
  public priorityPieOptions = _.cloneDeep(pieChartJson)
  public tagSearch: TagDictionary
  public searchQuery: ResolvedSearchResultData
  public presetDateFilters = PresetDateFilters
  public presetFilters = Object.keys(PresetDateFilters)
    .filter(
      (key) =>
        PresetDateFilters[key as keyof typeof PresetDateFilters] !==
        PresetDateFilters.CUSTOM,
    )
    .map((key) => PresetDateFilters[key as keyof typeof PresetDateFilters])
  public customFilter: PresetDateFilters = PresetDateFilters.CUSTOM
  public huntsByAssignee: Observable<AssignedHunts>
  @ViewChild('filterPanel') public filterPanel: ElementRef
  @ViewChild('pdf') public pdf: PDFExportComponent
  @ViewChild('reportHtml', { static: false }) public reportHtml!: ElementRef

  public element = document.getElementById('dashboardContent')
  public users: Array<Profile>
  public state: DashboardState
  public huntCount$: Observable<{ count: number }>
  public totalPackageCount$: Observable<{ count: number }>
  public uniquePackageCount$: Observable<{ count: number }>
  public searchLink: Observable<CyborgRouterSearchLink>
  private mitreHeatmapCoverageSubject = new BehaviorSubject<{
    results: Map<string, MitreHeatMapCoverage>
  }>({ results: new Map() })
  public mitreHeatmapCoverage$ = this.mitreHeatmapCoverageSubject.asObservable()
  public tactics$: ReplaySubject<MITRETactics[]>
  public tactics: MITRETactics[]
  public isLoading = false

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  )
  public readonly theme = this.themeService.theme

  constructor() {
    this.tooltipFormatter = this.tooltipFormatter.bind(this)
    this.labelFormatter = this.labelFormatter.bind(this)
    this.onHeatmapClick = this.onHeatmapClick.bind(this)
  }

  public ngOnInit(): void {
    this.tactics$ =
      cachedMitreSubject ??
      (this.httpClient
        .get(MitreComponent.mitreEnterpriseJsonUrl)
        .pipe(map(processMitreEnterpriseJSON), shareReplay(1)) as ReplaySubject<
        MITRETactics[]
      >)

    cachedMitreSubject = this.tactics$

    this.searchStateService.reset()

    const searchParams: Params = this.searchDataService.stateToTagParams(
      this.searchStateService.state.value,
    )

    this.searchDataService.getSearchResults(searchParams).subscribe((query) => {
      this.searchQuery = query
    })

    this.setDashboardDefaultSearchState()

    const tagParams: Params = this.searchDataService.stateToTagParams(
      this.searchStateService.state.value,
    )

    this.searchDataService.getTagResults(tagParams).subscribe((tags) => {
      this.tagSearch = tags
    })

    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments
    })

    this.companyService.getUsers().subscribe((users) => {
      this.users = users
    })
  }

  public ngAfterViewInit(): void {
    // Initialize ECharts instances
    this.mitreSunburst = echarts.init(
      document.getElementById('top-mitre-container'),
    )
    this.actorSunburst = echarts.init(
      document.getElementById('top-actors-container'),
    )
    this.threatPie = echarts.init(
      document.getElementById('top-threats-container'),
    )

    // MITRE
    this.patchMitreHeatmapPalette()
    this.mitreHeatmap = echarts.init(
      document.getElementById('mitre-heatmap-container'),
    )

    // Metrics
    this.statusPie = echarts.init(
      document.getElementById('status-pie-container'),
    )
    this.outcomePie = echarts.init(
      document.getElementById('outcome-pie-container'),
    )
    this.priorityPie = echarts.init(
      document.getElementById('priority-pie-container'),
    )

    this.mitreHeatmap.on('click', this.onHeatmapClick.bind(this))

    window.addEventListener('resize', () => {
      this.actorSunburst.resize()
      this.mitreSunburst.resize()
      this.mitreHeatmap.resize()
      this.outcomePie.resize()
      this.statusPie.resize()
      this.priorityPie.resize()
      this.threatPie.resize()
    })

    if (Object.keys(this.activatedRoute.snapshot.queryParams).length) {
      this.populateFromUrl(this.activatedRoute.snapshot.queryParams)
    } else {
      this.dashboardUrlService.gotoUrl(
        this.dashboardStateService.dashboardState.value,
      )
    }

    this.dashboardStateService.dashboardState
      .pipe(
        tap(() => {
          this.loading$.next(true)
          this.isLoaded = false
        }),
        switchMap((state) => {
          this.dashboardUrlService.gotoUrl(state)
          this.state = JSON.parse(JSON.stringify(state))
          // Assign observables tied to dashboardState
          const status$ = this.huntMetricsService.getDashboardHuntsByStatus(
            state.filter,
          )
          const priority$ = this.huntMetricsService.getDashboardHuntsByPriority(
            state.filter,
          )
          const outcome$ = this.huntMetricsService.getDashboardHuntsByOutcome(
            state.filter,
          )

          this.huntActorCoverage$ = this.huntMetricsService
            .getHuntActorCoverage(state.filter)
            .pipe(shareReplay(1))
          this.huntThreatCoverage$ = this.huntMetricsService
            .getHuntThreatCoverage(state.filter)
            .pipe(shareReplay(1))
          this.huntMITRECoverage$ = this.huntMetricsService
            .getHuntMITRECoverage(state.filter)
            .pipe(shareReplay(1))

          const mitreHeatmapCoverage$ = this.huntMetricsService
            .getHuntMITREHeatmapCoverage(state.filter)
            .pipe(
              tap((coverage) =>
                this.mitreHeatmapCoverageSubject.next(coverage),
              ),
            )
          this.huntCount$ = this.huntMetricsService
            .getDashboardHuntCount(state.filter)
            .pipe(
              shareReplay(1), // Cache the value for future subscriptions
            )
          this.totalPackageCount$ = this.huntMetricsService
            .getDashboardHuntPackageCount(state.filter)
            .pipe(shareReplay(1))
          this.uniquePackageCount$ = this.huntMetricsService
            .getUniqueDashboardHuntPackageCount(state.filter)
            .pipe(shareReplay(1))
          this.huntsByAssignee = this.huntMetricsService
            .getHuntsByAssignee(state.filter)
            .pipe(shareReplay(1))
          this.myAssignedHunts$ = this.huntMetricsService
            .getMyAssignedHunts(state.filter)
            .pipe(shareReplay(1))
          return forkJoin({
            status: status$,
            priority: priority$,
            outcome: outcome$,
            huntCount: this.huntCount$,
            actorCoverage: this.huntActorCoverage$,
            threatCoverage: this.huntThreatCoverage$,
            mitreCoverage: this.huntMITRECoverage$,
            mitreHeatmapCoverage: mitreHeatmapCoverage$,
          }).pipe(
            finalize(() => {
              this.loading$.next(false)
              this.isLoaded = true
            }), // Set loading to false when all API calls complete
          )
        }),
      )
      .subscribe((result) => {
        this.huntCount = result.huntCount.count

        // outcome pie
        result.outcome.results.map((item) => {
          item.color = mapOutcomeColor(item.outcome as Enums.OutcomeEnum)
          if (!item.outcome) item.outcome = 'No Outcome'
        })
        this.huntOutcomes = result.outcome.results.map(
          ({ outcome, color, count }) => ({
            value: Number(count),
            name: outcome as Enums.OutcomeEnum,
            itemStyle: { color: color },
          }),
        )
        this.outcomePieOptions.series[0].data = this.huntOutcomes
        this.outcomePieOptions.series[0].name = 'Outcome'
        this.outcomePie.setOption(this.outcomePieOptions)

        // priority pie
        result.priority.results.map((item) => {
          item.color = mapPriorityColor(item.priority)
        })
        this.huntPriorities = result.priority.results.map(
          ({ priority, color, count }) => ({
            value: Number(count),
            name: priority as Enums.PriorityEnum,
            itemStyle: { color: color },
          }),
        )

        this.priorityPieOptions.series[0].data = this.huntPriorities
        this.priorityPieOptions.series[0].name = 'Priority'
        this.priorityPie.setOption(this.priorityPieOptions)

        // status pie
        result.status.results.map((item) => {
          item.color = mapStatusColor(item.status)
        })
        this.huntStatuses = result.status.results.map(
          ({ status, color, count }) => ({
            value: Number(count),
            name: status as Enums.StatusEnum,
            itemStyle: { color: color },
          }),
        )

        this.statusPieOptions.series[0].data = this.huntStatuses
        this.statusPieOptions.series[0].name = 'Status'
        this.statusPie.setOption(this.statusPieOptions)

        if (result.mitreCoverage.results.length === 0) {
          this.mitreSunburst.setOption(this.noDataOptions, { notMerge: true })
        } else {
          const mitreSunburstResults: SunburstData[] =
            this.createCoverageSunburst(result.mitreCoverage.results)

          this.mitreSunburstOptions.series.name = 'Top 10 MITRE Techniques'
          this.mitreSunburstOptions.series.data = mitreSunburstResults

          this.mitreSunburst.setOption(this.mitreSunburstOptions, {
            notMerge: true,
          })
        }

        if (result.actorCoverage.results.length === 0) {
          this.actorSunburst.setOption(this.noDataOptions, { notMerge: true })
        } else {
          const actorSunburstResults: SunburstData[] =
            this.createCoverageSunburst(result.actorCoverage.results)
          this.actorSunburstOptions.series.name = 'Top 10 Actors'
          this.actorSunburstOptions.series.data = actorSunburstResults
          this.actorSunburst.setOption(this.actorSunburstOptions, {
            notMerge: true,
          })
        }
        if (result.threatCoverage.results.length === 0) {
          this.threatPie.setOption(this.noDataOptions, { notMerge: true })
        } else {
          const threatChartData = result.threatCoverage.results.map(
            ({ name, huntCount }, index) => ({
              value: Number(huntCount),
              name: addNewLinesBetweenWords(name),
              itemStyle: { color: this.chartColors[index] },
            }),
          )
          this.threatPieOptions.series[0].data = threatChartData.splice(0, 10)
          this.threatPieOptions.series[0].name = 'Top 10 Threats'
          this.threatPieOptions.series[0].name = 'Top 10 Threats'
          this.threatPie.setOption(
            { ...this.threatPieOptions },
            { notMerge: true },
          )
        }
      })

    combineLatest([
      this.mitreHeatmapCoverage$,
      this.tactics$,
      this.showAllMITREToggleSubject,
    ]).subscribe((results) => {
      const [mitreHeatmap, tactics, showAllMITREToggleSubject] = results
      this.tactics = tactics
      const mitreHeatmapCoverage = mitreHeatmap?.results

      this.mitreHeatmapOptions.tooltip.formatter =
        this.tooltipFormatter.bind(this)
      this.mitreHeatmapOptions.series[0].label.formatter =
        this.labelFormatter.bind(this)

      this.mitreHeatmapOptions.series[0].data = []
      let tacticNames: string[]
      if (showAllMITREToggleSubject) {
        tacticNames = tactics.map((tactic, index) => {
          for (let i = 0; i < tactic.children.length; i++) {
            const subarray = [index, i]
            const count =
              mitreHeatmapCoverage[
                tactic.children[i]?.external_references[0]?.external_id
              ]?.huntPackageTotalCount || 0

            subarray.push(count)
            this.mitreHeatmapOptions.series[0].data.push(subarray)
          }
          return addNewLinesBetweenWords(tactic.name)
        })
      } else {
        tacticNames = tactics.map((tactic, index) => {
          let ycount = 0
          for (let i = 0; i < tactic.children.length; i++) {
            const subarray = [index, ycount]
            const count =
              mitreHeatmapCoverage[
                tactic.children[i]?.external_references[0]?.external_id
              ]?.huntPackageTotalCount || 0

            subarray.push(i)
            subarray.push(count)
            if (count > 0) {
              ycount++
              this.mitreHeatmapOptions.series[0].data.push(subarray)
            }
          }
          return addNewLinesBetweenWords(tactic.name)
        })
      }

      this.longestHeatmapColumn = findLongestHeatmapColumn(
        this.mitreHeatmapOptions.series[0].data,
      )

      this.mitreHeatmapOptions.yAxis.data = new Array(
        this.longestHeatmapColumn,
      ).fill('') // List of all tactics

      this.mitreHeatmapOptions.xAxis.data = tacticNames
      this.mitreHeatmap.setOption(this.mitreHeatmapOptions)
    })

    // Trigger change detection manually.
    this.cdRef.detectChanges()
  }

  @HostListener('document:keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.popup.toggle(false)
    }
  }

  public cancelCustomDate(): void {
    this.calendar.selectionRange.start = null
    this.calendar.selectionRange.end = null
    this.dashboardStateService.getState().presetDateFilter =
      PresetDateFilters.CURRENT_MONTH
    this.anchorComponent.selected = false
  }
  private applyInlinePdfStyles = () => {
    const links = this.reportHtml.nativeElement.querySelectorAll('a')
    links.forEach((el: HTMLElement) => {
      el.style.color = '#ff3750'
      el.style.opacity = '1'
      el.style.visibility = 'visible'
      el.style.mixBlendMode = 'normal'
      el.style.isolation = 'auto'
      el.style.textShadow = 'none'
      el.style.filter = 'none'
      el.style.fontWeight = '400'
      el.style.textTransform = 'none'
      el.style.fontFamily = `'Roboto', 'Arial', 'sans-serif'`
      el.style.fontSize = '11pt'
    })

    const nodes = this.reportHtml.nativeElement.querySelectorAll(
      'p, span, li, h1, h2, h3, h4, h5, h6, td, th, pre',
    )
    nodes.forEach((el: HTMLElement) => {
      el.style.color = '#ffffff'
      el.style.opacity = '1'
      el.style.visibility = 'visible'
      el.style.mixBlendMode = 'normal'
      el.style.isolation = 'auto'
      el.style.textShadow = 'none'
      el.style.filter = 'none'
      el.style.fontWeight = '400'
      el.style.textTransform = 'none'
      el.style.fontFamily = `'Roboto', 'Arial', 'sans-serif'`
      el.style.fontSize = '11pt'
    })
  }

  public applyCustomDate(): void {
    this.dashboardStateService.getState().presetDateFilter =
      PresetDateFilters.CUSTOM

    this.dashboardStateService.getState().filter.filters =
      this.dashboardStateService
        .getState()
        .filter.filters.filter(
          (filter) => filter.field !== DashboardFilterFields.CREATED,
        )

    const filters: FilterDescriptor[] = []

    if (this.calendar.selectionRange.start) {
      filters.push({
        field: DashboardFilterFields.CREATED,
        operator: FilterOperator.GreaterThanOrEqual,
        value: this.calendar.selectionRange.start,
      })
    }

    if (this.calendar.selectionRange.end) {
      filters.push({
        field: DashboardFilterFields.CREATED,
        operator: FilterOperator.LessThanOrEqual,
        value: this.calendar.selectionRange.end,
      })
    } else {
      // if no end value, only look at selected day
      filters.push({
        field: DashboardFilterFields.CREATED,
        operator: FilterOperator.GreaterThanOrEqual,
        value: this.calendar.selectionRange.start,
      })
    }

    if (filters.length) {
      this.dashboardStateService.getState().filter.filters.push(...filters)
    }

    this.dashboardStateService.updateState()
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    let clickedElement: HTMLElement | null = event.target as HTMLElement
    let clickedDatePicker = false
    const clickedFilterButton = false
    const isAnchorClicked = this.anchor.nativeElement.contains(
      event.target as Node,
    )

    while (clickedElement) {
      if (
        clickedElement.tagName === 'KENDO-MULTIVIEWCALENDAR' ||
        clickedElement.tagName === 'KENDO-CALENDAR-HORIZONTAL'
      ) {
        clickedDatePicker = true
        break
      }
      clickedElement = clickedElement.parentElement
    }

    if (!isAnchorClicked && !clickedFilterButton && !clickedDatePicker) {
      this.popup.toggle(false) // Close filter panel if clicked outside and not inside date picker
    }
  }

  private populateFromUrl(queryParams: Params): void {
    let searchState: DashboardState =
      this.dashboardUrlService.searchStateFromString(queryParams)
    if (!searchState) {
      searchState = this.dashboardStateService.dashboardState.value
    }
    this.dashboardStateService.setState(searchState)
  }

  public exportDashboard(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      ExportOptionsDialogComponent,
      { data: Object.values(DashboardChartExports) },
    )

    dialogRef.afterClosed().subscribe((exportOptions) => {
      if (exportOptions) {
        prepareReportSections(exportOptions as string[])
        this.isExporting = true

        // Temporarily remove scroll from heatmap and increase container size so all data is visible in export
        if (
          (exportOptions as string[]).includes(
            DashboardChartExports.MITRE_HEATMAP,
          )
        ) {
          const { dataZoom, ...rest } = this.mitreHeatmapOptions
          const height: number =
            this.longestHeatmapColumn *
              this.mitreHeatmapOptions.series[0].boxSize[0] +
            parseFloat(
              (this.mitreHeatmapOptions.grid as GridOption).top as string,
            )

          const myDiv = document.getElementById('mitre-heatmap-container')
          if (myDiv) {
            myDiv.style.height = `${height}px`
          }

          this.mitreHeatmap.resize()
          this.mitreHeatmap.setOption(rest, true)
        }
        setTimeout(() => {
          this.applyInlinePdfStyles()
          this.pdf.saveAs(
            `hunt-module-dashboard-report-${new Date().toLocaleDateString()}.pdf`,
          )
          this.trackingService.trackEvent(
            TrackingEventEnum.HUNT_DASHBOARD_EXPORTED,
          )
          this.isExporting = false
          setTimeout(() => {
            resetReportSections(exportOptions as string[])
            this.mitreHeatmap.setOption(this.mitreHeatmapOptions)
          }, 2000)
        }, 1000)
      }
    })
  }

  public getFilterLabel(field: string, value: string): string {
    if (field == DashboardFilterFields.ENVIRONMENTS) {
      const matchedEnv = this.environments?.find((environment) => {
        return environment.id == value
      })
      return field + ': ' + matchedEnv?.name
    }
    return field + ': ' + value
  }

  public isDateRangeSelected(dateRangeFilter: PresetDateFilters): boolean {
    return this.dashboardStateService.getState()?.presetDateFilter
      ? dateRangeFilter ===
          this.dashboardStateService.getState().presetDateFilter
      : false
  }
  public getCustomDateFilterLabel(): string {
    const start = this.dashboardStateService
      .getState()
      .filter.filters.find((filter) => {
        return (
          filter.field === DashboardFilterFields.CREATED &&
          filter.operator === FilterOperator.GreaterThanOrEqual
        )
      })
    const end = this.dashboardStateService
      .getState()
      .filter.filters.find((filter) => {
        return (
          filter.field === DashboardFilterFields.CREATED &&
          filter.operator === FilterOperator.LessThanOrEqual
        )
      })

    if (start) {
      const startDate = new Date(start.value)
      if (start && end) {
        const endDate = new Date(end.value)

        const sameDay: boolean =
          startDate.getFullYear() === endDate.getFullYear() &&
          startDate.getMonth() === endDate.getMonth() &&
          startDate.getDate() === endDate.getDate()
        if (sameDay) {
          return (
            DashboardFilterFields.CREATED +
            ' on: ' +
            startDate.toLocaleDateString()
          )
        } else {
          return (
            DashboardFilterFields.CREATED +
            ' between: ' +
            startDate.toLocaleDateString() +
            ' - ' +
            endDate.toLocaleDateString()
          )
        }
      }
      return (
        DashboardFilterFields.CREATED + ' on: ' + startDate.toLocaleDateString()
      )
    }
  }
  public createCoverageSunburst(results: HuntCoverage[]): SunburstData[] {
    const sunburstData: SunburstData[] = []
    const country_set: Set<string> = new Set()
    let count = 0
    const topTenResults = results.slice().splice(0, 10)
    for (const result of topTenResults) {
      if (!country_set.has(result.category)) {
        const children: SunburstData[] = []
        count++
        children.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntCount,
        })
        sunburstData.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.category),
          children,
        })
        country_set.add(result.category)
      } else {
        const targetCategory = sunburstData.find(
          (data) => data.name == addNewLinesBetweenWords(result.category),
        )
        targetCategory.children.push({
          itemStyle: { color: `${targetCategory.itemStyle.color}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntCount,
        })
      }
    }
    return sunburstData
  }

  public getField(
    filter: CompositeFilterDescriptor | FilterDescriptor,
  ): string {
    return (filter as FilterDescriptor).field as string
  }

  public notDateFilter(filter: string): boolean {
    return filter !== DashboardFilterFields.CREATED
  }

  public getFilterLabelSingle(field: string, value: string): string {
    if (
      field == DashboardFilterFields.ASSIGNEE ||
      field === DashboardFilterFields.AUTHOR
    ) {
      const matchedUser = this.users?.find((user) => {
        return user.id === Number(value)
      })
      return field + ': ' + matchedUser?.firstName + ' ' + matchedUser?.lastName
    }
    return field + ': ' + value
  }

  public removeFilter(
    targetFilter: CompositeFilterDescriptor | FilterDescriptor,
    value?: number | string[],
  ): void {
    const filter = targetFilter as FilterDescriptor
    const field = filter.field as string

    // filterToRemove
    const currentState = this.dashboardStateService.getState()

    if (!Array.isArray(filter.value)) {
      // single select filters
      currentState.filter.filters = currentState.filter.filters.filter(
        (filter) => {
          return filter.field !== field && filter.value != value
        },
      )
    } else {
      // multi-select filters
      const filterIndex = currentState.filter.filters.findIndex(
        (filter) => filter.field === field,
      )
      if (filterIndex !== -1) {
        currentState.filter.filters[filterIndex].value =
          currentState.filter.filters[filterIndex].value.filter(
            (val) => val !== value,
          )
        if (currentState.filter.filters[filterIndex].value.length === 0) {
          currentState.filter.filters.splice(filterIndex, 1)
        }
      }
    }
    this.dashboardStateService.updateState(currentState)
  }

  public isMultiSelectFilter(filter: FilterDescriptor): boolean {
    return Array.isArray(filter.value)
  }

  public getProgress(userHunts: number): number {
    return Math.round((userHunts / this.huntCount) * 100)
  }

  public getHuntPackageCountForMITRE(mitreId: string): number {
    const match = this.tagSearch?.mitre_technique_ids.find(
      (id) => id.key === mitreId,
    )

    if (match) return match.doc_count
    return 0
  }

  public applyPresetFilter(presetFilter: PresetDateFilters): void {
    const today = new Date()
    let startDate = today

    switch (presetFilter) {
      case PresetDateFilters.CURRENT_MONTH:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1)
        break
      case PresetDateFilters.ONE_MONTH:
        startDate.setMonth(today.getMonth() - 1)
        break
      case PresetDateFilters.THREE_MONTHS:
        startDate.setMonth(today.getMonth() - 3)
        break
      case PresetDateFilters.ONE_YEAR:
        startDate.setFullYear(today.getFullYear() - 1)
        break
      case PresetDateFilters.YTD:
        startDate = new Date(today.getFullYear(), 0, 1)
        break
      default:
        break
    }

    if (startDate) {
      this.dashboardStateService.getState().filter.filters =
        this.dashboardStateService
          .getState()
          .filter.filters.filter(
            (filter) => filter.field !== DashboardFilterFields.CREATED,
          )

      const filters: FilterDescriptor[] = []

      if (startDate) {
        filters.push({
          field: DashboardFilterFields.CREATED,
          operator: FilterOperator.GreaterThanOrEqual,
          value: startDate,
        })
      }

      if (filters.length) {
        this.dashboardStateService.getState().filter.filters.push(...filters)
        this.dashboardStateService.getState().presetDateFilter = presetFilter

        this.dashboardStateService.updateState()
      }
    }
  }

  public getHuntPackageCountForActor(actor: string): number {
    const match = this.tagSearch?.actors.find((id) => id.key === actor)

    if (match) return match.doc_count
    return 0
  }

  public setSearchStateAndNavigate(
    tag: Enums.CyborgTagTypeEnum,
    value: string | Array<string>,
  ): void {
    this.setDashboardDefaultSearchState()

    if (tag === Enums.CyborgTagTypeEnum.THREAT_NAMES)
      this.searchStateService.addFilter(
        Enums.CyborgTagTypeEnum.THREAT_NAMES,
        value,
      )
    if (tag === Enums.CyborgTagTypeEnum.ACTORS)
      this.searchStateService.addFilter(Enums.CyborgTagTypeEnum.ACTORS, value)
    if (tag === Enums.CyborgTagTypeEnum.MITRE_TECHNIQUE_IDS)
      this.searchStateService.addFilter(
        Enums.CyborgTagTypeEnum.MITRE_TECHNIQUE_IDS,
        value,
      )
    if (tag === Enums.CyborgTagTypeEnum.MITRE_TECHNIQUE_NAMES)
      this.searchStateService.addFilter(
        Enums.CyborgTagTypeEnum.MITRE_TECHNIQUE_NAMES,
        value,
      )

    const searchLink: CyborgRouterSearchLink =
      this.searchUrlService.stateToRouterLink(
        this.searchStateService.state.getValue(),
      )

    this.router.navigate(searchLink.routerLink as string[], {
      queryParams: searchLink.queryParams,
    })
  }

  /*
   * Set default search state in the context of hunting - which means only look at hunt packages and huntable content
   */
  public setDashboardDefaultSearchState(): void {
    this.searchStateService.reset()

    this.searchStateService.setLibrary(
      [Enums.CyborgIndexEnum.CYBORG_USE_CASES],
      true,
    )

    this.searchStateService.addFilter(
      Enums.CyborgTagTypeEnum.GOALS,
      Enums.Goals.HUNT,
    )
    this.searchStateService.addFilter(
      Enums.CyborgTagTypeEnum.GOALS,
      Enums.Goals.VISUAL,
    )
  }

  public getHuntPackageCountForThreat(threatName: string): number {
    const match = this.tagSearch?.threat_names.find(
      (id) => id.key === threatName,
    )

    if (match) return match.doc_count
    return 0
  }

  /**
   * Generate hover tooltips for mitre heatmap cells - minimal or maximal view depending on value of showAllMITREToggleSubject
   * @param params - heatmap series data
   */
  public tooltipFormatter(params: { value: number[] }): string {
    if (this.showAllMITREToggleSubject.value) {
      let tooltip = ''
      const technique = this.tactics[params.value[0]].children[params.value[1]]
      const techniqueId = technique.external_references[0].external_id
      const techCoverage =
        this.mitreHeatmapCoverageSubject.value?.results[techniqueId]
          ?.huntPackageCount || 0
      tooltip +=
        techniqueId + ' ' + technique.name + ': ' + +techCoverage + '<br>'
      this.tactics[params.value[0]].children[
        params.value[1]
      ]?.sub_techniques.forEach((sub) => {
        const subId = sub?.external_references[0].external_id
        const subCoverage = this.mitreHeatmapCoverageSubject.value?.results[
          techniqueId
        ]?.subtechniques?.find((subtech) => subtech?.name === subId)
        const subNumber = subCoverage?.huntPackageCount || 0
        tooltip += subId + ' ' + sub.name + ': ' + subNumber + '<br>'
      })
      return tooltip
    } else {
      let tooltip = ''
      const technique = this.tactics[params.value[0]].children[params.value[2]]
      const techniqueId = technique.external_references[0].external_id

      const techCoverage =
        this.mitreHeatmapCoverageSubject.value?.results[techniqueId]

      const techNumber = techCoverage?.huntPackageCount || 0

      if (techNumber > 0) {
        tooltip +=
          techniqueId + ' ' + technique.name + ': ' + techNumber + '<br>'
      }

      if (
        technique?.sub_techniques.length > 0 &&
        this.mitreHeatmapCoverageSubject.value?.results[techniqueId]
          ?.subtechniques?.length > 0
      )
        this.tactics[params.value[0]].children[
          params.value[2]
        ]?.sub_techniques.forEach((sub) => {
          const subId = sub?.external_references[0].external_id
          const subCoverage = this.mitreHeatmapCoverageSubject.value?.results[
            techniqueId
          ]?.subtechniques?.find((subtech) => subtech?.name === subId)
          const subNumber = subCoverage?.huntPackageCount || 0

          if (subNumber > 0) {
            tooltip += subId + ' ' + sub.name + ': ' + subNumber + '<br>'
          }
        })
      return tooltip
    }
  }

  /**
   * Generate labels for mitre heatmap cells - target param value indices vary depending on value
   * of showAllMITREToggleSubject due to limitations of echarts series data https://echarts.apache.org/v4/en/option.html#series-heatmap.data
   * @param params - heatmap series data
   */
  public labelFormatter(params: any): string {
    if (this.showAllMITREToggleSubject.value) {
      return (
        addNewLinesBetweenWords(
          this.tactics[params.value[0]].children[params.value[1]].name,
        ) +
        ' \n' +
        params.value[2]
      )
    } else {
      return (
        addNewLinesBetweenWords(
          this.tactics[params.value[0]].children[params.value[2]].name,
        ) +
        ' \n' +
        params.value[3]
      )
    }
  }

  /**
   * When heatmap cell is clicked, apply target mitre technique to search state and navigate to search
   * @param params - heatmap series data
   */
  public onHeatmapClick(params: any): void {
    if (this.showAllMITREToggleSubject.value === true) {
      const techniqueAndSubTags = this.tagSearch?.mitre_technique_ids.filter(
        (str) =>
          str.key.startsWith(
            this.tactics[params.value[0]].children[params.value[1]]
              .external_references[0].external_id,
          ),
      )
      const techniqueAndSubIds = techniqueAndSubTags.map((obj) => obj.key)

      this.setSearchStateAndNavigate(
        Enums.CyborgTagTypeEnum.MITRE_TECHNIQUE_IDS,
        techniqueAndSubIds,
      )
    } else {
      const techniqueAndSubTags = this.tagSearch?.mitre_technique_ids.filter(
        (str) =>
          str.key.startsWith(
            this.tactics[params.value[0]].children[params.value[2]]
              .external_references[0].external_id,
          ),
      )
      const techniqueAndSubIds = techniqueAndSubTags.map((obj) => obj.key)

      this.setSearchStateAndNavigate(
        Enums.CyborgTagTypeEnum.MITRE_TECHNIQUE_IDS,
        techniqueAndSubIds,
      )
    }
  }

  /**
   * Apply target assignee id to hunt state and navigate
   * @param id - target assignee id
   */
  public setHuntStateAssignee(id: string): void {
    const state: ExtendedState = {
      filter: {
        filters: [
          {
            field: DashboardFilterFields.ASSIGNEE,
            operator: FilterOperator.EqualTo,
            value: Number(id),
          },
        ],
        logic: 'and',
      },
      archived: false,
    }

    this.navigateToHuntState(state)
  }

  /**
   * Apply target hunt ids to hunt state and navigate
   * @param hunts - list of target hunt ids
   */
  public setHuntStateId(hunts: { id: string; name: string }[]): void {
    const huntIds = hunts.map((obj) => obj.id)

    const state: ExtendedState = {
      filter: {
        filters: [
          {
            field: HuntFilterFields.ID,
            operator: FilterOperator.EqualTo,
            value: [...huntIds],
          },
        ],
        logic: 'and',
      },
      archived: false,
    }
    this.navigateToHuntState(state)
  }

  /**
   * Apply hunt state and navigate to hunt list
   * @param state - hunt state
   */
  public navigateToHuntState(state: ExtendedState): void {
    this.huntStateService.reset()
    this.huntStateService.updateState(state)
    const huntRouterLink: CyborgRouterSearchLink =
      this.huntUrlService.stateToRouterLink(state)
    this.router.navigate(huntRouterLink.routerLink as string[], {
      queryParams: huntRouterLink.queryParams,
    })
  }
}
